import { Button, Table } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import Api from "../../api";
import { getGameName } from "../../Components/charts/StackedBarChart";
import { showError } from "../../utils";
import { Session } from "../Dashboard";
import { ExportOutlined, HistoryOutlined } from "@ant-design/icons";
import FileSaver from "file-saver";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { SectionCard } from "../../Components/SectionCard";

dayjs.extend(utc);

type Props = {
  // When provided, the component uses these sessions directly (and skips its
  // own fetch). When omitted, it fetches all sessions on mount.
  sessions?: Session[];
  description?: string;
};

export const SessionOverview = (props: Props) => {
  const externallyProvided = props.sessions !== undefined;
  const [fetchedSessions, setFetchedSessions] = useState<Session[]>();

  useEffect(() => {
    if (externallyProvided) return;
    const fetchSessions = async () => {
      try {
        const newSessions: Session[] = await Api.get(
          "/api/stats/games_played",
          {}
        );
        setFetchedSessions(newSessions);
      } catch (error) {
        showError("Failed to get sessions", error);
      }
    };
    fetchSessions();
  }, [externallyProvided]);

  const sessions = useMemo(() => {
    const source = externallyProvided ? props.sessions : fetchedSessions;
    if (!source) return undefined;
    return [...source].sort(
      (a, b) =>
        parseUtcTimestamp(b.timestamp).valueOf() -
        parseUtcTimestamp(a.timestamp).valueOf()
    );
  }, [externallyProvided, props.sessions, fetchedSessions]);

  const exportCsv = () => {
    const rows: string[] = ["Date Time,Player Count,Game,Credits"];
    sessions?.forEach((session) => {
      rows.push(
        renderSessionDateTimeCsv(session.timestamp) +
          "," +
          (session.player_count || 0).toString() +
          "," +
          getGameName(session.game) +
          "," +
          (session.credits || 0)
      );
    });
    downloadCSV(rows.join("\n"));
  };
  return (
    <SectionCard
      icon={<HistoryOutlined />}
      title="Session Overview"
      description={
        props.description ?? "Every game session played at your location"
      }
      actions={
        <Button onClick={exportCsv} icon={<ExportOutlined />}>
          Export CSV
        </Button>
      }
      noPadding
    >
      <Table
        style={{ overflowY: "auto", maxHeight: "500px" }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        size="small"
        dataSource={sessions}
        columns={columns}
        scroll={{ x: "max-content" }}
      />
    </SectionCard>
  );
};

const columns = [
  {
    title: "Date",
    dataIndex: "timestamp",
    key: "date_time",
    render: (x: string) => renderSessionDateTime(x),
  },
  {
    title: "Game",
    dataIndex: "game",
    key: "game",
    render: (x: string) => getGameName(x),
  },
  {
    title: "Player Count",
    dataIndex: "player_count",
    key: "player_count",
  },
  {
    title: "Credits",
    dataIndex: "credits",
    key: "credits",
  },
];

function renderSessionDateTime(x: string) {
  const date = parseUtcTimestamp(x);
  if (!date.isValid()) return x;

  const localDate = date.local().toDate();
  const formattedDate = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(localDate);
  const formattedTime = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(localDate);

  return `${formattedDate} ${formattedTime}`;
}

function renderSessionDateTimeCsv(x: string) {
  const date = parseUtcTimestamp(x);
  if (!date.isValid()) return x;

  // Fixed format prevents Excel from mixed locale date guessing.
  return date.local().format("YYYY-MM-DD HH:mm");
}

function parseUtcTimestamp(timestamp: string) {
  // server timestamp is stored without timezone info, in UTC
  const normalized = timestamp.trim().replace(" ", "T");
  return dayjs.utc(normalized);
}

async function downloadCSV(contents: string) {
  try {
    const file = new Blob([contents], { type: "text/csv" });
    FileSaver.saveAs(file, "sessionOverview.csv");
  } catch (error) {
    showError("Failed to export csv", error);
  }
}
